import { Show, createEffect, createSignal } from 'solid-js';
import { ActivityIndicator, Button, FieldDescription, Input, Label, TextField } from '@troon/ui';
import { useAction, useSubmission } from '@solidjs/router';
import debounce from 'debounce';
import { IconCircleCheck } from '@troon/icons/circle-check';
import { clientAction } from '@troon/api-client';
import { useTrackEvent } from '@troon/analytics';
import { useAuthStore } from '../flow';

export function ForgotPassword() {
	const [store, setStore] = useAuthStore();
	const [fakeSubmitting, setFakeSubmitting] = createSignal(false);
	const trackEvent = useTrackEvent();

	const handleReset = useAction(resetPassword);
	const resetSubmission = useSubmission(resetPassword);

	const handleChange = debounce((value: string) => {
		setStore('data', 'confirmationCode', value);
	}, 100);

	createEffect(() => {
		if (resetSubmission.pending) {
			trackEvent('passwordResetCode');
		}
		if (resetSubmission.result) {
			setTimeout(() => {
				resetSubmission.clear();
			}, 500);
		}
	});

	return (
		<form
			class="flex flex-col gap-4"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				setFakeSubmitting(true);
				setTimeout(() => {
					setFakeSubmitting(false);
					setStore('step', '/auth/reset/password');
				}, 500);
			}}
		>
			<Show when={store.data.email}>
				<input type="hidden" name="email" value={store.data.email} />
			</Show>

			<TextField name="confirmationCode">
				<Label>Confirmation code</Label>
				<Input value={store.data.confirmationCode} onInput={(e) => handleChange(e.currentTarget.value)} />
				<FieldDescription>Enter the confirmation code found in the email.</FieldDescription>
				<FieldDescription>
					<p class="flex flex-row items-center text-sm">
						Didn't receive the email?{' '}
						<Button
							type="button"
							class="inline-flex max-w-min shrink p-px font-normal text-nowrap normal-case"
							appearance="transparent"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								const data = new FormData();
								data.set('emailOrRewardsId', store.data.email!);
								data.set('redirectUrl', store.redirect);
								handleReset(data);
							}}
						>
							Resend it now
						</Button>
						.
						<Show when={resetSubmission.pending}>
							<ActivityIndicator class="ms-2 size-4 text-brand" />
						</Show>
						<Show when={resetSubmission.result}>
							<IconCircleCheck class="ms-2 size-4 text-green-500" title="Confirmation code sent!" />
						</Show>
					</p>
				</FieldDescription>
			</TextField>

			<Button type="submit" disabled={fakeSubmitting()}>
				<Show when={fakeSubmitting()}>
					<ActivityIndicator aria-label="Loading" class="me-2 size-4" />
				</Show>
				Check code
			</Button>
		</form>
	);
}

const resetPassword = clientAction('post', '/v0/auth/forgot-password');
